import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from '../components/Toast';
import { getOrderDetails, cancelOrder, requestReturn, getOrderInvoice, getReturnReasons } from '../api/order.api';
import OrderStatusBadge from '../components/OrderStatusBadge';
import { getOrderPriceSummary } from '../utils/orderPricing';
import { resolveAssetUrl } from '../config';
import { getProgressRemarkForEntry } from '../utils/orderProgressNotes';

const formatKES = (amount) => 'KES ' + Number(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

const CANCELLABLE_STATUSES = ['pending_payment', 'confirmed', 'processing'];
const RETURNABLE_STATUSES = ['delivered'];
const TERMINAL_STATUSES = new Set([
    'delivered',
    'cancelled',
    'refunded',
    'returned',
    'return_requested',
    'payment_failed',
]);
const FAILED_STATUSES = new Set(['cancelled', 'payment_failed']);

export default function OrderDetail() {
    const { orderNumber } = useParams();
    const navigate = useNavigate();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Cancel modal
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);

    // Return modal
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [returnReason, setReturnReason] = useState('');
    const [returnDescription, setReturnDescription] = useState('');
    const [returning, setReturning] = useState(false);
    const [returnReasons, setReturnReasons] = useState([]);
    const [returnWindowHours, setReturnWindowHours] = useState(24);

    // Invoice download
    const [downloadingInvoice, setDownloadingInvoice] = useState(false);

    useEffect(() => {
        if (orderNumber) {
            document.title = `Order #${orderNumber} | Fone Express`;
        }
    }, [orderNumber]);

    const fetchOrder = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getOrderDetails(orderNumber);
            const resData = res?.data || res;
            if (resData?.isOk && resData?.data) {
                setOrder(resData.data);
            } else {
                setError('Order not found.');
            }
        } catch (err) {
            console.error('Error fetching order:', err);
            setError('Unable to load order details. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [orderNumber]);

    useEffect(() => {
        fetchOrder();
    }, [fetchOrder]);

    useEffect(() => {
        (async () => {
            try {
                const res = await getReturnReasons();
                const resData = res?.data || res;
                if (resData?.isOk && resData?.data) {
                    setReturnReasons(resData.data.reasons || []);
                    setReturnWindowHours(resData.data.returnWindowHours || 24);
                }
            } catch (err) {
                console.error('Failed to load return reasons', err);
            }
        })();
    }, []);

    const handleCancel = async () => {
        if (!cancelReason.trim()) {
            toast.error('Please provide a reason for cancellation');
            return;
        }
        setCancelling(true);
        try {
            const res = await cancelOrder(orderNumber, { reason: cancelReason.trim() });
            const resData = res?.data || res;
            if (resData?.isOk) {
                toast.success('Order cancelled successfully');
                setShowCancelModal(false);
                setCancelReason('');
                fetchOrder();
            } else {
                toast.error(resData?.message || 'Failed to cancel order');
            }
        } catch (err) {
            console.error('Error cancelling order:', err);
            toast.error('Failed to cancel order. Please try again.');
        } finally {
            setCancelling(false);
        }
    };

    const handleReturn = async () => {
        if (!returnReason) {
            toast.error('Please select a reason for return');
            return;
        }
        const selected = returnReasons.find((r) => r.slug === returnReason);
        if (selected?.requiresDescription && !returnDescription.trim()) {
            toast.error('Please provide more details for this return reason');
            return;
        }
        setReturning(true);
        try {
            const res = await requestReturn(orderNumber, {
                reason: returnReason,
                description: returnDescription.trim(),
            });
            const resData = res?.data || res;
            if (resData?.isOk) {
                toast.success('Return request submitted successfully');
                setShowReturnModal(false);
                setReturnReason('');
                setReturnDescription('');
                fetchOrder();
            } else {
                toast.error(resData?.message || 'Failed to submit return request');
            }
        } catch (err) {
            console.error('Error requesting return:', err);
            toast.error(
                err?.response?.data?.message ||
                    'Failed to submit return request. Please try again.',
            );
        } finally {
            setReturning(false);
        }
    };

    const handleDownloadInvoice = async () => {
        setDownloadingInvoice(true);
        try {
            const response = await getOrderInvoice(orderNumber);
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `invoice-${orderNumber}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success('Invoice downloaded');
        } catch (err) {
            console.error('Error downloading invoice:', err);
            toast.error('Failed to download invoice');
        } finally {
            setDownloadingInvoice(false);
        }
    };

    // Loading Skeleton
    if (loading) {
        return (
            <div className="animate-pulse space-y-6">
                <div className="h-6 bg-muted rounded w-48"></div>
                <div className="h-4 bg-muted rounded w-32"></div>
                <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                    <div className="h-4 bg-muted rounded w-full"></div>
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                    <div className="h-20 bg-muted rounded"></div>
                    <div className="h-20 bg-muted rounded"></div>
                </div>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div className="text-center py-12">
                <i className="ri-error-warning-line text-4xl text-red-400 mb-4 block"></i>
                <p className="text-muted-foreground mb-4">{error}</p>
                <button
                    onClick={fetchOrder}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all whitespace-nowrap"
                >
                    Try Again
                </button>
            </div>
        );
    }

    if (!order) return null;

    const deliveredAt =
        order.shipping?.deliveredAt ||
        order.delivery?.deliveredAt ||
        order.pickup?.collectedAt;
    const hoursSinceDelivery = deliveredAt
        ? (Date.now() - new Date(deliveredAt).getTime()) / (1000 * 60 * 60)
        : null;
    const withinReturnWindow =
        hoursSinceDelivery != null && hoursSinceDelivery <= returnWindowHours;
    // One return attempt per order — block after any prior return (including rejected)
    const hasPriorReturn =
        Boolean(order.return?.requestedAt || order.return?.status) ||
        (order.statusHistory || []).some((e) => e.status === 'return_requested');
    const canReturn =
        RETURNABLE_STATUSES.includes(order.status) &&
        withinReturnWindow &&
        !hasPriorReturn;
    const canCancel = CANCELLABLE_STATUSES.includes(order.status);
    const canDownloadInvoice = order.status !== 'pending_payment' && order.status !== 'payment_failed';
    const selectedReturnReason = returnReasons.find((r) => r.slug === returnReason);

    const returnReasonLine = (() => {
        if (order.return?.reasonLabel || order.return?.reason) {
            const label = order.return.reasonLabel || order.return.reason;
            const extra = order.return.description?.trim();
            return extra ? `${label} - ${extra}` : label;
        }
        const hist = order.statusHistory || [];
        const req = [...hist].reverse().find((e) => e.status === 'return_requested');
        if (!req?.note) return null;
        return String(req.note).replace(/^return requested:\s*/i, '').trim() || null;
    })();

    const returnDecisionLines = (order.statusHistory || [])
        .map((entry, idx, list) => {
            const remark = getProgressRemarkForEntry(list, idx);
            if (!remark) return null;
            if (entry.status === 'return_requested') return null;
            if (entry.status === 'returned') {
                return { label: 'Return approved', text: remark };
            }
            if (
                entry.status === 'delivered' &&
                idx > 0 &&
                list[idx - 1]?.status === 'return_requested'
            ) {
                return { label: 'Return rejected', text: remark };
            }
            if (entry.status === 'refunded') {
                return { label: 'Refunded', text: remark };
            }
            return null;
        })
        .filter(Boolean);

    return (
        <div>
            {/* Back Button */}
            <button
                onClick={() => navigate('/account/orders')}
                className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors mb-4 text-sm"
            >
                <i className="ri-arrow-left-line"></i>
                Back to Orders
            </button>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">
                        Order #{order.orderNumber}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Placed on{' '}
                        {new Date(order.createdAt).toLocaleDateString('en-KE', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <OrderStatusBadge status={order.status} />
                    {order.paymentStatus && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${order.paymentStatus === 'paid'
                                ? 'bg-green-100 text-green-800'
                                : order.paymentStatus === 'failed'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            {(order.paymentStatus || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                    )}
                </div>
            </div>

            {/* Status Timeline */}
            {order.statusHistory && order.statusHistory.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-6 mb-6 overflow-x-auto">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Order Progress</h2>
                    <div className="flex items-start min-w-max">
                        {order.statusHistory.map((entry, idx) => {
                            const isLast = idx === order.statusHistory.length - 1;
                            const isTerminal = TERMINAL_STATUSES.has(entry.status);
                            const isFailed = FAILED_STATUSES.has(entry.status);
                            const isComplete = !isLast || isTerminal;
                            const isReturnRejected =
                                entry.status === 'delivered' &&
                                idx > 0 &&
                                order.statusHistory[idx - 1]?.status === 'return_requested';
                            const statusLabel = isReturnRejected
                                ? 'Return Rejected'
                                : (entry.status || '')
                                    .replace(/_/g, ' ')
                                    .replace(/\b\w/g, c => c.toUpperCase());
                            return (
                                <div key={idx} className="flex items-start flex-1 min-w-[120px]">
                                    <div className="flex flex-col items-center">
                                        <div
                                            className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                                !isComplete
                                                    ? 'bg-primary'
                                                    : (isFailed && isLast) || isReturnRejected
                                                      ? 'bg-red-500'
                                                      : 'bg-green-500'
                                            }`}
                                        >
                                            {!isComplete ? (
                                                <i className="ri-time-line text-sm"></i>
                                            ) : (isFailed && isLast) || isReturnRejected ? (
                                                <i className="ri-close-line text-sm"></i>
                                            ) : (
                                                <i className="ri-check-line text-sm"></i>
                                            )}
                                        </div>
                                        <p className="text-xs font-medium text-foreground mt-2 text-center">
                                            {statusLabel}
                                        </p>
                                        <p className="text-[10px] text-muted-foreground mt-1 text-center">
                                            {new Date(entry.timestamp || entry.date).toLocaleDateString('en-KE', {
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            })}
                                        </p>
                                    </div>
                                    {!isLast && (
                                        <div className="flex-1 h-0.5 bg-green-500 mt-4 mx-2"></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {(returnReasonLine || returnDecisionLines.length > 0) && (
                        <div className="mt-4 space-y-1">
                            {returnReasonLine && (
                                <p className="text-[11px] text-muted-foreground leading-snug break-words">
                                    * Return reason: {returnReasonLine}
                                </p>
                            )}
                            {returnDecisionLines.map((line, i) => (
                                <p
                                    key={`${line.label}-${i}`}
                                    className="text-[11px] text-muted-foreground leading-snug break-words"
                                >
                                    * {line.label}: {line.text}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Store Pickup Transfer Status */}
            {order.fulfillmentType === 'store_pickup' && order.pickup && (
                <div className="bg-card border border-border rounded-lg p-6 mb-6">
                    <h2 className="text-lg font-semibold text-foreground mb-4">
                        <i className="ri-store-2-line mr-2 text-primary"></i>
                        Store Pickup Status
                    </h2>

                    {/* Store info */}
                    <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg mb-5 border border-orange-100">
                        <i className="ri-map-pin-2-line text-primary text-lg mt-0.5"></i>
                        <div>
                            <p className="font-semibold text-foreground text-sm">{order.pickup.storeName}</p>
                            {order.pickup.storeAddress && (
                                <p className="text-xs text-muted-foreground mt-0.5">{order.pickup.storeAddress}</p>
                            )}
                        </div>
                    </div>

                    {/* Transfer steps */}
                    {(() => {
                        const pickupStatus = order.pickup.status || 'pending';
                        const transfer = order.pickup.transferId;
                        const transferStatus = transfer?.status || 'pending';

                        const steps = [
                            {
                                key: 'order_placed',
                                label: 'Order Placed',
                                desc: 'Your order has been received',
                                done: true,
                                active: false,
                            },
                            {
                                key: 'awaiting_approval',
                                label: 'Awaiting Approval',
                                desc: transfer
                                    ? `Transfer #${transfer.transfer_number} created`
                                    : 'Awaiting payment confirmation',
                                done: !!transfer,
                                active: !!transfer && transfer.approval_status === 'pending',
                            },
                            {
                                key: 'transfer_approved',
                                label: 'Transfer Approved',
                                desc: transfer?.approval_status === 'approved'
                                    ? 'Transfer has been approved by admin'
                                    : 'Awaiting admin approval',
                                done: !!transfer && transfer.approval_status === 'approved',
                                active: !!transfer && transfer.approval_status === 'approved' && transferStatus === 'pending',
                            },
                            {
                                key: 'in_transit',
                                label: 'Stock in Transit',
                                desc: transfer?.shipped_date
                                    ? `Dispatched on ${new Date(transfer.shipped_date).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })}`
                                    : 'Awaiting dispatch from warehouse',
                                done: ['shipped', 'received'].includes(transferStatus),
                                active: transferStatus === 'shipped',
                            },
                            {
                                key: 'ready',
                                label: 'Ready for Pickup',
                                desc: order.pickup.readyAt
                                    ? `Available since ${new Date(order.pickup.readyAt).toLocaleDateString('en-KE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                                    : 'Stock arriving at store',
                                done: ['ready', 'collected'].includes(pickupStatus),
                                active: pickupStatus === 'ready',
                            },
                            {
                                key: 'collected',
                                label: 'Collected',
                                desc: order.pickup.collectedAt
                                    ? `Collected on ${new Date(order.pickup.collectedAt).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })}`
                                    : 'Not yet collected',
                                done: pickupStatus === 'collected',
                                active: false,
                            },
                        ];

                        return (
                            <div className="space-y-0">
                                {steps.map((step, idx) => (
                                    <div key={step.key} className="flex gap-4">
                                        {/* Step indicator column */}
                                        <div className="flex flex-col items-center">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${
                                                step.done
                                                    ? 'bg-green-500 border-green-500 text-white'
                                                    : step.active
                                                        ? 'bg-primary border-primary text-white'
                                                        : 'bg-card border-border text-muted-foreground'
                                            }`}>
                                                {step.done ? (
                                                    <i className="ri-check-line text-sm"></i>
                                                ) : step.active ? (
                                                    <i className="ri-loader-4-line text-sm animate-spin"></i>
                                                ) : (
                                                    <span className="text-xs font-bold">{idx + 1}</span>
                                                )}
                                            </div>
                                            {idx < steps.length - 1 && (
                                                <div className={`w-0.5 flex-1 min-h-[24px] my-1 ${step.done ? 'bg-green-400' : 'bg-border'}`}></div>
                                            )}
                                        </div>
                                        {/* Step text */}
                                        <div className="pb-5 pt-1">
                                            <p className={`text-sm font-semibold ${step.done ? 'text-green-700' : step.active ? 'text-primary' : 'text-muted-foreground'}`}>
                                                {step.label}
                                                {step.active && <span className="ml-2 text-xs font-normal">In progress</span>}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}

                    {/* Security notice when ready */}
                    {order.pickup.status === 'ready' && (
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-xs text-amber-800">
                                <strong>Security reminder:</strong> Please bring your order number and a valid photo ID when collecting.
                                Do not share your order details or receipt with anyone — anyone with this information can collect your items.
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Order Items */}
            <div className="bg-card border border-border rounded-lg mb-6">
                <div className="p-4 border-b border-border">
                    <h2 className="text-lg font-semibold text-foreground">
                        Items ({(order.items || []).length})
                    </h2>
                </div>
                <div className="divide-y divide-border">
                    {(order.items || []).map((item, idx) => (
                        <div key={idx} className="p-4 flex gap-4">
                            <Link
                                to={
                                    item.slug || item.variantSlug || item.productSlug
                                        ? `/product/${item.slug || item.variantSlug || item.productSlug}`
                                        : '#'
                                }
                                onClick={(e) => {
                                    if (!(item.slug || item.variantSlug || item.productSlug)) {
                                        e.preventDefault();
                                    }
                                }}
                                className="w-20 h-20 border border-border rounded-lg overflow-hidden bg-muted flex-shrink-0"
                            >
                                <img
                                    src={resolveAssetUrl(item.image || item.productImage)}
                                    alt={item.productName || 'Product'}
                                    className="w-full h-full object-contain p-1"
                                />
                            </Link>
                            <div className="flex-1 min-w-0">
                                <Link
                                    to={
                                        item.slug || item.variantSlug || item.productSlug
                                            ? `/product/${item.slug || item.variantSlug || item.productSlug}`
                                            : '#'
                                    }
                                    onClick={(e) => {
                                        if (!(item.slug || item.variantSlug || item.productSlug)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    className="font-medium text-foreground hover:text-primary transition-colors line-clamp-2"
                                >
                                    {item.productName}
                                </Link>
                                {(item.variantInfo || item.color || item.attributes) && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {item.variantInfo || [
                                            item.color && `Color: ${item.color}`,
                                            ...(item.attributes
                                                ? Object.entries(item.attributes).map(
                                                    ([k, v]) => `${k}: ${v}`
                                                )
                                                : []),
                                        ].filter(Boolean).join(' | ')}
                                    </p>
                                )}
                                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm">
                                    <span className="text-muted-foreground">Qty: {item.quantity}</span>
                                    <span className="text-muted-foreground">
                                        Unit: {formatKES(item.unitPrice || item.price)}
                                    </span>
                                    {item.discount > 0 && (
                                        <span className="text-green-600">
                                            -{formatKES(item.discount)}
                                        </span>
                                    )}
                                    <span className="font-semibold text-foreground">
                                        {formatKES(item.lineTotal || item.total)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Price Summary & Shipping Info Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Price Summary */}
                <div className="bg-card border border-border rounded-lg p-4">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Price Summary</h2>
                    <div className="space-y-2 text-sm">
                        {(() => {
                            const priceSummary = getOrderPriceSummary(order);
                            return priceSummary.summaryRows.map((row) => (
                                <div key={row.label} className={`flex justify-between ${row.isInformational ? 'text-xs' : 'text-sm'}`}>
                                    <span className={
                                        row.isDiscount
                                            ? 'text-green-600'
                                            : row.isInformational
                                                ? 'text-muted-foreground/80'
                                                : 'text-muted-foreground'
                                    }>
                                        {row.label}
                                    </span>
                                    <span className={
                                        row.isDiscount
                                            ? 'text-green-600'
                                            : row.isInformational
                                                ? 'text-muted-foreground/80'
                                                : 'text-foreground'
                                    }>
                                        {row.isDiscount ? `-${formatKES(Math.abs(row.value))}` : formatKES(row.value)}
                                    </span>
                                </div>
                            ));
                        })()}
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Shipping</span>
                            <span className="text-foreground">
                                {(order.pricing?.shippingCharge || order.shippingFee) > 0 ? formatKES(order.pricing?.shippingCharge || order.shippingFee) : 'Free'}
                            </span>
                        </div>
                        {(order.pricing?.codFee || order.codFee) > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">COD Fee</span>
                                <span className="text-foreground">{formatKES(order.pricing?.codFee || order.codFee)}</span>
                            </div>
                        )}
                        {(order.pricing?.storeCreditApplied || order.storeCredit) > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Store Credit</span>
                                <span className="text-green-600">-{formatKES(order.pricing?.storeCreditApplied || order.storeCredit)}</span>
                            </div>
                        )}
                        {order.pricing?.loyaltyPointsDiscount > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Loyalty Points</span>
                                <span className="text-green-600">-{formatKES(order.pricing.loyaltyPointsDiscount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between pt-2 border-t border-border font-semibold text-base">
                            <span className="text-foreground">Grand Total</span>
                            <span className="text-primary">{formatKES(order.pricing?.grandTotal || order.grandTotal)}</span>
                        </div>
                    </div>
                </div>

                {/* Shipping Info */}
                <div className="bg-card border border-border rounded-lg p-4">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Shipping Information</h2>
                    <div className="space-y-3 text-sm">
                        {order.shippingAddress && (
                            <div>
                                <p className="text-muted-foreground mb-1">Delivery Address</p>
                                <p className="text-foreground">
                                    {[
                                        order.shippingAddress.fullName,
                                        order.shippingAddress.addressLine1,
                                        order.shippingAddress.addressLine2,
                                        order.shippingAddress.city,
                                        order.shippingAddress.county,
                                        order.shippingAddress.postalCode,
                                    ].filter(Boolean).join(', ')}
                                </p>
                                {order.shippingAddress.phone && (
                                    <p className="text-muted-foreground mt-1">
                                        Phone: {order.shippingAddress.phone}
                                    </p>
                                )}
                            </div>
                        )}
                        {(order.delivery?.provider || order.shipping?.logisticsName || order.logisticsProvider) && (
                            <div>
                                <p className="text-muted-foreground mb-1">Delivery Partner</p>
                                <p className="text-foreground font-medium">
                                    {order.delivery?.provider === 'fargo'
                                        ? '🚚 Fargo Courier (Standard Delivery)'
                                        : order.delivery?.provider === 'littlecab'
                                            ? '⚡ LittleCab (Express Delivery)'
                                            : order.delivery?.provider === 'manual'
                                                ? '🚚 Delivery'
                                                : order.shipping?.logisticsName || order.logisticsProvider}
                                </p>
                                {order.delivery?.promisedText && (
                                    <p className="text-sm text-blue-600 font-medium mt-1">
                                        {order.delivery.promisedText}
                                    </p>
                                )}
                            </div>
                        )}
                        {(order.shipping?.trackingNumber || order.trackingNumber) && (
                            <div>
                                <p className="text-muted-foreground mb-1">Tracking Number</p>
                                {(order.shipping?.trackingUrl || order.trackingUrl) ? (
                                    <a
                                        href={order.shipping?.trackingUrl || order.trackingUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                    >
                                        {order.shipping?.trackingNumber || order.trackingNumber}
                                        <i className="ri-external-link-line ml-1"></i>
                                    </a>
                                ) : (
                                    <p className="text-foreground">{order.shipping?.trackingNumber || order.trackingNumber}</p>
                                )}
                            </div>
                        )}
                        {(order.shipping?.estimatedDelivery || order.estimatedDeliveryDate) && (
                            <div>
                                <p className="text-muted-foreground mb-1">Estimated Delivery</p>
                                <p className="text-foreground">
                                    {new Date(order.shipping?.estimatedDelivery || order.estimatedDeliveryDate).toLocaleDateString('en-KE', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                    })}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
                {canCancel && (
                    <button
                        onClick={() => setShowCancelModal(true)}
                        className="px-4 py-2 border-2 border-red-500 text-red-500 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors whitespace-nowrap"
                    >
                        <i className="ri-close-circle-line mr-1"></i>
                        Cancel Order
                    </button>
                )}
                {canReturn && (
                    <button
                        onClick={() => setShowReturnModal(true)}
                        className="px-4 py-2 border-2 border-orange-500 text-orange-500 rounded-lg text-sm font-medium hover:bg-orange-50 transition-colors whitespace-nowrap"
                    >
                        <i className="ri-arrow-go-back-line mr-1"></i>
                        Request Return
                    </button>
                )}
                {canDownloadInvoice && (
                    <button
                        onClick={handleDownloadInvoice}
                        disabled={downloadingInvoice}
                        className="px-4 py-2 border-2 border-border text-foreground rounded-lg text-sm font-medium hover:border-primary hover:text-primary transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                        <i className={`${downloadingInvoice ? 'ri-loader-4-line animate-spin' : 'ri-download-line'} mr-1`}></i>
                        {downloadingInvoice
                            ? 'Downloading...'
                            : order.status === 'refunded'
                              ? 'Download Refunded Invoice'
                              : 'Download Invoice'}
                    </button>
                )}
            </div>

            {/* Cancel Order Modal */}
            {showCancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setShowCancelModal(false)}
                    ></div>
                    <div className="relative bg-card rounded-lg w-full max-w-md p-6 shadow-xl">
                        <h3 className="text-lg font-semibold text-foreground mb-2">Cancel Order</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Are you sure you want to cancel order #{order.orderNumber}? This action cannot be undone.
                        </p>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Reason for cancellation <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            rows={3}
                            placeholder="Please tell us why you want to cancel..."
                            className="w-full border border-border rounded-lg p-3 text-sm focus:outline-none focus:border-primary resize-none"
                        ></textarea>
                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                onClick={() => setShowCancelModal(false)}
                                className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted transition-colors whitespace-nowrap"
                            >
                                Go Back
                            </button>
                            <button
                                onClick={handleCancel}
                                disabled={cancelling || !cancelReason.trim()}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:brightness-110 transition-all disabled:opacity-50 whitespace-nowrap"
                            >
                                {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Return Order Modal */}
            {showReturnModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setShowReturnModal(false)}
                    ></div>
                    <div className="relative bg-card rounded-lg w-full max-w-md p-6 shadow-xl">
                        <h3 className="text-lg font-semibold text-foreground mb-2">Request Return</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Please select a reason below.
                        </p>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Reason <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={returnReason}
                            onChange={(e) => setReturnReason(e.target.value)}
                            className="w-full border border-border rounded-lg p-3 text-sm focus:outline-none focus:border-primary mb-4"
                        >
                            <option value="">Select a reason</option>
                            {returnReasons.map((r) => (
                                <option key={r.slug} value={r.slug}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Additional Details
                            {selectedReturnReason?.requiresDescription && (
                                <span className="text-red-500"> *</span>
                            )}
                        </label>
                        <textarea
                            value={returnDescription}
                            onChange={(e) => setReturnDescription(e.target.value)}
                            rows={3}
                            placeholder="Any additional details about the return..."
                            className="w-full border border-border rounded-lg p-3 text-sm focus:outline-none focus:border-primary resize-none"
                        ></textarea>
                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                onClick={() => setShowReturnModal(false)}
                                className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted transition-colors whitespace-nowrap"
                            >
                                Go Back
                            </button>
                            <button
                                onClick={handleReturn}
                                disabled={returning || !returnReason}
                                className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:brightness-110 transition-all disabled:opacity-50 whitespace-nowrap"
                            >
                                {returning ? 'Submitting...' : 'Submit Return'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
